import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CameraIcon,
  CalendarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const TemplateSelection = ({ formData, updateFormData, onNext }) => {
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  const templates = [
    {
      id: 'client_projects',
      title: 'Client Projects',
      description: 'Manage photoshoots from booking to delivery',
      icon: CameraIcon,
      color: 'from-[#FFB27D] to-[#FF9D5C]'
    },
    {
      id: 'shoot_calendar',
      title: 'Shoot Calendar',
      description: 'Schedule and organize photography sessions',
      icon: CalendarIcon,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'client_gallery',
      title: 'Client Gallery',
      description: 'Share and organize client photo deliverables',
      icon: DocumentTextIcon,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'team_tasks',
      title: 'Team Tasks',
      description: 'Assign editing, retouching, and admin work',
      icon: ClipboardDocumentListIcon,
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'equipment_tracker',
      title: 'Equipment Tracker',
      description: 'Manage camera gear, lenses, and studio equipment',
      icon: ChartBarIcon,
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'client_hub',
      title: 'Client Hub',
      description: 'Centralize client communications and contracts',
      icon: UserGroupIcon,
      color: 'from-pink-500 to-pink-600'
    }
  ];

  const toggleTemplate = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  const handleContinue = () => {
    updateFormData({ templates: selectedTemplates });
    onNext();
  };

  return (
    <div className="px-8 py-12 max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h2 className="text-4xl font-bold text-white mb-6">
          What are you planning on using Klorah for?
        </h2>
        <p className="text-xl text-white/70">
          You can always add more later
        </p>
      </motion.div>

      <div className="flex gap-8">
        {/* Template Grid */}
        <div className="flex-1">
          <div className="grid grid-cols-2 gap-6">
            {templates.map((template, index) => (
              <motion.button
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => toggleTemplate(template.id)}
                className={`p-6 rounded-2xl border transition-all duration-300 text-left group relative ${
                  selectedTemplates.includes(template.id)
                    ? 'border-brand bg-brand/10 shadow-lg shadow-orange-500/20'
                    : 'border-white/10 hover:border-brand/50 hover:bg-white/5 bg-white/5'
                }`}
              >
                {selectedTemplates.includes(template.id) && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-4 right-4 w-6 h-6 bg-brand rounded-full flex items-center justify-center shadow-lg"
                  >
                    <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </motion.div>
                )}

                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${template.color} shadow-lg`}>
                    <template.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold text-lg transition-colors ${
                      selectedTemplates.includes(template.id)
                        ? 'text-brand'
                        : 'text-white group-hover:text-brand'
                    }`}>
                      {template.title}
                    </h3>
                    <p className="text-sm text-white/70 mt-2">
                      {template.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="w-80 bg-white/5 rounded-2xl p-6 border border-white/10">
          <div className="text-center">
            <h3 className="font-semibold text-white mb-4 text-lg">
              Preview
            </h3>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
              <div className="text-sm text-white/50 mb-3">
                Your Workspace
              </div>
              <div className="space-y-3">
                {selectedTemplates.length > 0 ? (
                  selectedTemplates.map(templateId => {
                    const template = templates.find(t => t.id === templateId);
                    return (
                      <div key={templateId} className="flex items-center gap-3 text-sm">
                        <div className={`w-4 h-4 rounded bg-gradient-to-br ${template.color} shadow-sm`} />
                        <span className="text-white/90">{template.title}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-white/40 text-sm">
                    Select templates to see preview
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm text-white/50">
              You can customize this later
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-12">
        <button
          onClick={handleContinue}
          className="px-8 py-3 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default TemplateSelection;